import { Injectable, InjectionToken, Signal, computed, inject, signal } from '@angular/core';
import { environment } from '../../environments/environment';

export interface ReachabilityProbe {
  /** Resolve true only if the API actually answered. Never throws. */
  check(): Promise<boolean>;
}

/** Probes GET /api/health. The path must stay under /api: nginx proxies only /api/*, so any
 *  other path is answered by the SPA fallback with a 200 — which would report "online" in
 *  airplane mode. /api/* is also never ngsw-cached (there are no dataGroups), so this always
 *  hits the wire. */
export class HttpReachabilityProbe implements ReachabilityProbe {
  async check(): Promise<boolean> {
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 3000);
    try {
      const res = await fetch(`${environment.apiUrl}/api/health`, {
        cache: 'no-store',
        signal: ctrl.signal,
      });
      return res.ok;
    } catch {
      return false;
    } finally {
      clearTimeout(timer);
    }
  }
}

export const REACHABILITY_PROBE = new InjectionToken<ReachabilityProbe>('REACHABILITY_PROBE', {
  providedIn: 'root',
  factory: () => new HttpReachabilityProbe(),
});

/** Verified connectivity. `navigator.onLine` alone reports "attached to a network", not "can
 *  reach the server" — on a weak-signal job site it reports online while every request fails.
 *  The two signals are asymmetric: onLine === false is trustworthy (definitely offline, skip
 *  the probe); onLine === true only means attached, so it must be verified. */
@Injectable({ providedIn: 'root' })
export class ConnectivityService {
  private probe = inject(REACHABILITY_PROBE);

  private static readonly BACKOFF_MS = [5_000, 10_000, 30_000, 60_000];

  private readonly navOnline = signal(typeof navigator === 'undefined' ? true : navigator.onLine);
  private readonly probeOk = signal(true);

  readonly online: Signal<boolean> = computed(() => this.navOnline() && this.probeOk());

  private timer: ReturnType<typeof setTimeout> | null = null;
  private backoffIdx = -1;
  private workProbes: Array<() => boolean> = [];
  // A throwing probe is treated as "no work", not propagated: registerWorkProbe accepts probes
  // this service does not control, and a bad one must not take out scheduleBackoff()'s caller
  // (markUnreachable(), reached from the interceptor's error handler).
  private hasQueuedWork = (): boolean => this.workProbes.some(p => {
    try { return p(); } catch { return false; }
  });
  private drains: Array<() => Promise<void>> = [];

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('online', () => { this.navOnline.set(true); void this.verify(); });
      window.addEventListener('offline', () => { this.navOnline.set(false); this.resetBackoffLadder(); });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') void this.verify();
      });
    }
  }

  /** Register a predicate reporting whether this queue has anything waiting to sync. Backoff
   *  polling runs while ANY registered queue reports work, so an idle app never polls. Additive:
   *  a single slot would mean an empty form outbox suppressed polling for every other queue. */
  registerWorkProbe(fn: () => boolean): void { this.workProbes.push(fn); }

  /** Register a queue's drain. Fired on every unreachable→reachable transition — the trigger
   *  that was missing: `registerWorkProbe` only says WHETHER to keep polling, never WHAT to
   *  drain, so a recovered connection updated the UI signal and nothing else. */
  registerDrain(fn: () => Promise<void>): void { this.drains.push(fn); }

  /** Whether any registered queue still has work to sync — e.g. to refuse leaving an
   *  impersonated session while its writes would replay without X-Act-As. */
  hasPendingWork(): boolean { return this.hasQueuedWork(); }

  /** Record reachability and, on a false→true transition only, drain every registered queue.
   *  Transition-only is what keeps a SINGLE queue's drain from re-entering itself: the request
   *  it makes comes back through noteReachable(), finds probeOk already true, and stops there.
   *  It is not full mutual exclusion across queues — a markUnreachable() from one queue while
   *  another's drain is still in flight can re-fire the whole set — but each queue's flush()
   *  coalesces concurrent calls, so the extra pass loses no work, just re-checks. */
  private async setReachable(ok: boolean): Promise<void> {
    const was = this.probeOk();
    this.probeOk.set(ok);
    if (!ok || was) return;
    // One queue's failure must not strand the others. Wrapping the call itself in
    // Promise.resolve().then(...) also catches a SYNCHRONOUS throw from a drain — without it,
    // that throw would skip .catch entirely and abort the map before the remaining drains run.
    await Promise.all(this.drains.map(d => Promise.resolve().then(d).catch(() => {})));
  }

  /** Probe the API and update `online`. Resolves only once the drains it triggered have run,
   *  so callers (and tests) have a deterministic join point. */
  async verify(): Promise<boolean> {
    if (!this.navOnline()) { await this.setReachable(false); return false; }
    const ok = await this.probe.check();
    // A probe success is a genuine recovery check (an actual /api/health round trip), so it
    // resets the ladder — see resetBackoffLadder() vs. clearBackoffTimer() below.
    if (ok) this.resetBackoffLadder(); else this.scheduleBackoff();
    await this.setReachable(ok);
    return ok;
  }

  /** A request that failed with no HTTP status proves the server is unreachable — record it
   *  immediately rather than waiting on a probe. */
  markUnreachable(): void {
    void this.setReachable(false);   // never awaits: drains fire on true only
    this.scheduleBackoff();
  }

  /** A successful API response proves the server is reachable right now, which is faster and
   *  cheaper than waiting for the next backoff probe — cancel the pending one. It deliberately
   *  does NOT reset the escalation ladder (see clearBackoffTimer() vs. resetBackoffLadder()):
   *  on a genuinely flapping link, some ordinary requests succeed while others still fail with
   *  status 0, and resetting backoffIdx on every one of those successes would pin the poll
   *  cadence at the first 5s rung forever instead of escalating. Only a proven recovery — the
   *  backoff timer's own probe succeeding in verify(), or the browser's 'offline' event — resets
   *  the ladder. */
  noteReachable(): void {
    this.clearBackoffTimer();
    void this.setReachable(true);
  }

  private scheduleBackoff(): void {
    if (this.timer || !this.hasQueuedWork()) return;
    this.backoffIdx = Math.min(this.backoffIdx + 1, ConnectivityService.BACKOFF_MS.length - 1);
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.verify();
    }, ConnectivityService.BACKOFF_MS[this.backoffIdx]);
  }

  /** Cancel the pending backoff timer without touching the ladder position. */
  private clearBackoffTimer(): void {
    if (this.timer) { clearTimeout(this.timer); this.timer = null; }
  }

  /** Cancel the pending timer AND reset the ladder to its first rung — reserved for a proven
   *  recovery (see call sites), not every successful response. */
  private resetBackoffLadder(): void {
    this.clearBackoffTimer();
    this.backoffIdx = -1;
  }
}
